/**
 * Kill-Cam PLAYER — the orchestrator that ties the rolling buffer, the scene director, the camera rig, the audio
 * and the exporter into one shareable moment.
 *
 * Flow: main.ts feeds `recordFrame`/`recordEvent` every sea frame (the SAME interpolated state it draws). On a
 * kill/death it calls `armHighlight` → a big, pulsing "★ SAVE HIGHLIGHT · Tap to save this kill" prompt appears
 * low-centre and PERSISTS (until tapped, a fresh kill re-arms it, or a respawn/port ends the moment — not a flash).
 * A tap (or the hotkey /
 * headless `trigger`) freezes the window, composes a director cut and TAKES OVER the frame loop: local prediction
 * is off (invariant #1 — everything interpolated), the HUD slides away, cinematic bars + colour grade + music
 * come up, the director plays a VARIED set of shots ending on the BULLET_TIME climax, and the whole thing is
 * recorded into a vertical 9:16 clip with the music + watermark baked in. At the end an export card offers Share
 * / Save. Everything degrades gracefully where MediaRecorder is unavailable (the cinematic still plays).
 *
 * Zero wire, zero sim, zero server. Renders through the shared renderer exactly like DragaScene does.
 */
import { REPLAY, BULLET_TIME, EventType, PROTOCOL_VERSION } from '@piratas/protocol';
import { buildTape, type KillTape } from './killtape.js';
import type { Renderer, LocalShipView } from './render.js';
import type { Audio } from './audio.js';
import type { InterpShip, InterpBall } from './net.js';
import { ReplayBuffer, makeSamplePools, sampleScene, type ShipLike, type BallLike, type RepEnv, type SamplePools, type FrozenReplay } from './replaybuffer.js';
import { composeCut, type Cut } from './shotdirector.js';
import { DirectorCamera, scrubTimeAt, clamp01, type Lookup } from './directorcamera.js';
import { ReplayExport, shareReplay, downloadReplay } from './replayexport.js';

type Phase = 'idle' | 'playing' | 'ended';

/** Scenario knobs for the E2E demo/analyze hooks (client-only theatre): force the ship separation + a sink despawn. */
export interface DemoOpts { dist?: number; sinkDespawn?: boolean; }
/** Numeric render-proof of one composed cut (all text — no screenshot needed). `minShipClip` < 0 = camera inside a hull. */
export interface KillcamProbe { kind: string; dist: number; sinkDespawn: boolean; duration: number; beats: string[]; byBullet: boolean; anyNaN: boolean; minCamHeight: number; minShipClip: number; maxFrameJump: number; nearVerticalFrames: number; }

export class ReplayPlayer {
  private buffer = new ReplayBuffer();
  private dcam = new DirectorCamera();
  private exporter: ReplayExport;
  private pools: SamplePools = makeSamplePools();
  private cut: Cut | null = null;
  private frozen: FrozenReplay | null = null;
  private phase: Phase = 'idle';
  private R = 0;            // real seconds into the cut
  private heroId = 0;
  private origFov = 55;
  private curScrubT = 0;
  private firedImpact = false;
  private firedMuzzle = false;   // the killer's muzzle-flash FX has been re-spawned for the clip (once)
  private spawnedWreck = false;  // the victim's sink cinematic has been kicked off for the clip (once)
  private victimCls = 1;         // the doomed hull class (drives the wreck mesh on a scrubbed clock)
  private fadeK = 0;             // 0..1 closing fade-to-black (last REPLAY.FADE_S of the cut)
  private recStarted = false;
  private clip: Blob | null = null;
  private clipMime = 'video/webm';
  private hadAudio = false;
  private skipHeld = false;

  // arming
  private armFreezeTimer: ReturnType<typeof setTimeout> | null = null;
  private armBtnTimer: ReturnType<typeof setTimeout> | null = null;
  private armedFrozen: FrozenReplay | null = null;
  private armedHeroId = 0;
  private pendingKillT = 0;
  private pendingHeroId = 0;

  // DOM
  private btn: HTMLButtonElement | null = null;
  private root: HTMLDivElement | null = null;
  private barTop!: HTMLDivElement; private barBot!: HTMLDivElement; private vig!: HTMLDivElement; private fadeEl!: HTMLDivElement;
  private titleEl!: HTMLDivElement; private skipEl!: HTMLButtonElement; private card!: HTMLDivElement;
  private gameCanvas: HTMLCanvasElement;

  // reused per-frame arrays (no steady-state allocation)
  private _remotes: InterpShip[] = [];
  private _balls: InterpBall[] = [];

  constructor(private renderer: Renderer, private audio: Audio, private overlay: HTMLElement, private haptic: (p: number | number[]) => void) {
    this.gameCanvas = renderer.renderer.domElement;
    this.exporter = new ReplayExport(this.gameCanvas);
  }

  /* --------------------------------- recording (from main.ts) --------------------------------- */

  recordFrame(t: number, self: ShipLike | null, ships: readonly ShipLike[], balls: readonly BallLike[], env: RepEnv): void {
    if (this.phase !== 'idle') return; // paused while a replay owns the loop
    this.buffer.recordFrame(t, self, ships, balls, env);
  }
  recordEvent(kind: number, t: number, x: number, y: number, a = 0, b = 0, c = 0): void {
    if (this.phase !== 'idle') return;
    this.buffer.recordEvent(kind, t, x, y, a, b, c);
  }
  /** Wipe the tape (death/respawn baseline resets, port transitions) so a clip never splices across a teleport.
   *  This also ENDS the highlight moment — dismiss the ★ prompt so it never lingers into a fresh life/port (the
   *  prompt otherwise persists, by design, until the captain taps it or a new kill re-arms it). */
  clearTape(): void { this.buffer.clear(); this.dismissButton(); }

  isActive(): boolean { return this.phase !== 'idle'; }
  ready(): boolean { return this.buffer.ready(); }

  /* --------------------------------- arming (the pulsing button) --------------------------------- */

  /** A kill/death happened. Freeze the window ~TAIL after the sink (so the sink itself is on the tape) and show
   *  the "★ SAVE HIGHLIGHT" prompt. It PERSISTS (ARM_MS is a long backstop, not a flash) until the captain taps it,
   *  a fresh kill re-arms it, or a respawn/port transition clears the tape. Re-arming resets both timers. */
  armHighlight(killT: number, heroId: number): void {
    if (this.phase !== 'idle' || !this.buffer.ready()) return;
    if (this.armFreezeTimer) clearTimeout(this.armFreezeTimer);
    if (this.armBtnTimer) clearTimeout(this.armBtnTimer);
    this.armedFrozen = null; this.armedHeroId = heroId; this.pendingKillT = killT; this.pendingHeroId = heroId;
    this.armFreezeTimer = setTimeout(() => { this.armFreezeTimer = null; this.armedFrozen = this.buffer.freeze(killT); }, REPLAY.TAIL_S * 1000);
    this.showButton();
    this.armBtnTimer = setTimeout(() => this.dismissButton(), REPLAY.ARM_MS);
  }

  private showButton(): void {
    if (!this.btn) {
      const b = document.createElement('button');
      b.id = 'kc-arm';
      // Big, unmissable, EXPLICIT CTA (mobile-first): a two-line label that says what it is AND what to do, a large
      // touch target, and a low-centre position clear of the crowded top HUD strip (topbar / map / toasts) so a
      // captain fresh off a kill can't miss it. All styling lives in KILLCAM_CSS (#kc-arm) — see there.
      b.innerHTML = '<span class="kc-arm-star">★</span><span class="kc-arm-txt"><span class="kc-arm-title">SAVE HIGHLIGHT</span><span class="kc-arm-sub">Tap to save this kill</span></span>';
      b.addEventListener('pointerdown', (e) => { e.preventDefault(); this.audio.ui(); this.haptic([12, 20, 12]); this.onArmTap(); });
      this.ensureStyle();
      document.body.appendChild(b);
      this.btn = b;
    }
    const b = this.btn;
    b.style.display = 'flex';
    // fade-in entrance (transform is owned by the pulse animation, so we ease OPACITY only — no conflict)
    b.style.transition = 'none'; b.style.opacity = '0';
    void b.offsetWidth; // reflow so the transition takes
    b.style.transition = 'opacity .35s ease-out'; b.style.opacity = '1';
  }
  private dismissButton(): void {
    if (this.armBtnTimer) { clearTimeout(this.armBtnTimer); this.armBtnTimer = null; }
    if (this.btn) this.btn.style.display = 'none';
  }
  private onArmTap(): void {
    let frozen = this.armedFrozen;
    if (!frozen) { if (this.armFreezeTimer) { clearTimeout(this.armFreezeTimer); this.armFreezeTimer = null; } frozen = this.buffer.freeze(this.pendingKillT); }
    this.dismissButton();
    void this.trigger(frozen, this.armedHeroId || this.pendingHeroId);
  }

  /* --------------------------------- trigger + playback --------------------------------- */

  /** Headless/manual entry: freeze the current tape and play. `heroId` defaults to the last-armed hero. */
  triggerNow(heroId?: number): boolean {
    if (this.phase !== 'idle' || !this.buffer.ready()) return false;
    const killT = this.pendingKillT || this.buffer.latestT();
    if (killT <= 0) return false;
    void this.trigger(this.buffer.freeze(killT), heroId ?? this.armedHeroId ?? this.heroId ?? 0);
    return true;
  }

  /** Keyboard hotkey: play the armed highlight if one is pending, else capture the current tape (a scenic clip). */
  hotkey(heroId?: number): boolean {
    if (this.phase !== 'idle') return false;
    if (this.armedFrozen || this.armFreezeTimer) { this.onArmTap(); return true; }
    return this.triggerNow(heroId);
  }

  /** E2E / render-proof theatre (like __cc.playDraga): SEED a scripted kill into the buffer and play it. Grants
   *  nothing — pure client visuals over the real renderer. `kind` picks the climax: cannon (bullet-time), ram, or
   *  flood. `cx,cy` should be the hero's live sim position so the scene sits over real ocean.
   *  `opts.dist` forces the ship separation (metres) to stage CLOSE-range vs long-range kills; `opts.sinkDespawn`
   *  drops the victim from the tape shortly after the sink to reproduce the real client despawning it. */
  demo(kind: 'cannon' | 'ram' | 'flood', nowT: number, cx: number, cy: number, heroId: number, opts: DemoOpts = {}): boolean {
    if (this.phase !== 'idle') return false;
    const killT = this.seedDemo(kind, nowT, cx, cy, heroId, opts);
    this.pendingKillT = killT; this.pendingHeroId = heroId;
    return this.triggerNow(heroId);
  }

  /** Play a captured/offline kill TAPE (a real acervo tape or a `dumpKill()` export) through the real renderer +
   *  director — for the owner (or a headless render-proof) to WATCH a real kill under the parametric director. Feeds
   *  the tape's verbatim frozen window straight to the same trigger the live ★ button uses. Zero wire, zero sim. */
  playTape(tape: KillTape): boolean {
    if (this.phase !== 'idle' || !tape || !tape.frozen || tape.frozen.frames.length < 2) return false;
    void this.trigger(tape.frozen, tape.heroId);
    return true;
  }

  /** Seed the rolling buffer with a scripted kill (shared by demo() + analyze()). Returns the kill sim-time. */
  private seedDemo(kind: 'cannon' | 'ram' | 'flood', nowT: number, cx: number, cy: number, heroId: number, opts: DemoOpts): number {
    this.buffer.clear();
    const enemyId = 99991, ballId = 99950;
    const dist = Math.max(2, opts.dist ?? 26);
    const ex = cx + dist, ey = cy + 5;                                 // the doomed enemy, off the hero's beam
    const despawnAfter = opts.sinkDespawn ? 0.3 : Infinity;            // seconds after the sink the victim leaves the AOI
    const env = { night: kind === 'flood' ? 0.7 : 0, storm: kind === 'flood' ? 0.6 : 0, windDir: 0.5, windStrength: 1.3 };
    const killT = nowT;
    for (let t = nowT - REPLAY.WINDOW_S; t <= nowT + REPLAY.TAIL_S + 1e-3; t += 0.05) {
      const k = (t - (nowT - REPLAY.WINDOW_S)) / REPLAY.WINDOW_S;
      const hero: ShipLike = { id: heroId, x: cx - 34 + k * 34, y: cy, heading: 0, speed: 6, sail: 1, hp: 100, flags: 0, cls: 1 };
      const sinking = t >= killT;
      const gone = t > killT + despawnAfter;                           // victim despawned (dropped from snapshots)
      const enemies: ShipLike[] = gone ? [] : [{ id: enemyId, x: ex, y: ey, heading: Math.PI, speed: 2, sail: 1, hp: sinking ? 0 : 100, flags: sinking ? 1 : 0, cls: 2 }];
      const balls: BallLike[] = [];
      if (kind === 'cannon' && t >= killT - 0.8 && t < killT) {
        const bk = (t - (killT - 0.8)) / 0.8, sx = cx - 4;
        balls.push({ id: ballId, x: sx + bk * (ex - sx), y: cy + bk * (ey - cy), heading: Math.atan2(ey - cy, ex - sx), elev: 1 });
      }
      this.buffer.recordFrame(t, hero, enemies, balls, env);
    }
    if (kind === 'cannon') { this.buffer.recordEvent(EventType.FIRE, killT - 1.0, cx - 4, cy, heroId, 1, 0); this.buffer.recordEvent(EventType.HIT, killT - 0.08, ex, ey, heroId, enemyId, 0); }
    if (kind === 'ram') this.buffer.recordEvent(EventType.RAM, killT - 0.05, ex, ey, 200, 0, 0);
    // v56: the SINK carries the FATAL BALL ID (c) — for a cannon kill that's the seeded hero ball; 0 otherwise.
    this.buffer.recordEvent(EventType.SINK, killT, ex, ey, enemyId, kind === 'flood' ? 0 : heroId, kind === 'cannon' ? ballId : 0);
    return killT;
  }

  /** RENDER-PROOF numeric probe (headless-friendly, no playback): seed a scripted kill, compose the real cut, and
   *  walk the real DirectorCamera over the REAL ocean — reporting duration + any NaN / hull-clip / camera-fly /
   *  gimbal-flip. Lets an agent prove the camera is smooth at any distance + through a sink, all as text. */
  analyze(kind: 'cannon' | 'ram' | 'flood', nowT: number, cx: number, cy: number, heroId: number, opts: DemoOpts = {}): KillcamProbe | null {
    if (this.phase !== 'idle') return null;
    this.seedDemo(kind, nowT, cx, cy, heroId, opts);
    const frozen = this.buffer.freeze(nowT);
    if (frozen.frames.length < 2) return null;
    const cut = composeCut(frozen, heroId);
    const dcam = new DirectorCamera(); dcam.baseFov = 55;
    const pools = makeSamplePools();
    const cache = new Map<number, { x: number; y: number; heading: number }>();
    let scrubT = frozen.t0;
    const seaY = (x: number, y: number): number => this.renderer.oceanY(x, y, scrubT);
    const look: Lookup = (id: number) => {
      let hit: { x: number; y: number; heading?: number } | null = null;
      if (pools.hasSelf && pools.self.id === id) hit = pools.self;
      if (!hit) for (let k = 0; k < pools.shipN; k++) if (pools.ships[k]!.id === id) { hit = pools.ships[k]!; break; }
      if (!hit) for (let k = 0; k < pools.ballN; k++) if (pools.balls[k]!.id === id) { hit = pools.balls[k]!; break; }
      if (hit) { cache.set(id, { x: hit.x, y: hit.y, heading: (hit as { heading?: number }).heading ?? 0 }); return hit; }
      return cache.get(id) ?? null;
    };
    const HR = 11, MAST = 13; // base hull footprint radius + mast height (world metres)
    let anyNaN = false, minCamH = Infinity, minClip = Infinity, maxJump = 0, flips = 0;
    let prev: { x: number; y: number; z: number } | null = null;
    const total = cut.totalReal, step = 1 / 60;
    for (let R = 0; R <= total; R += step) {
      const i = DirectorCamera.beatAt(cut, Math.min(R, total - 1e-4));
      const beat = cut.beats[i]!;
      scrubT = scrubTimeAt(beat, R - beat.realStart);
      sampleScene(frozen, scrubT, pools);
      const cam = dcam.sample(cut, Math.min(R, total - 1e-4), look, seaY, REPLAY.CROSSFADE_S);
      for (const v of [cam.px, cam.py, cam.pz, cam.lx, cam.ly, cam.lz, cam.fov]) if (!Number.isFinite(v)) anyNaN = true;
      minCamH = Math.min(minCamH, cam.py - seaY(cam.px, cam.pz));
      for (const id of [heroId, 99991]) { const s = look(id); if (!s) continue; if (cam.py < seaY(s.x, s.y) + MAST) minClip = Math.min(minClip, Math.hypot(cam.px - s.x, cam.pz - s.y) - HR); }
      if (prev) maxJump = Math.max(maxJump, Math.hypot(cam.px - prev.x, cam.py - prev.y, cam.pz - prev.z));
      prev = { x: cam.px, y: cam.py, z: cam.pz };
      const fx = cam.lx - cam.px, fy = cam.ly - cam.py, fz = cam.lz - cam.pz, fl = Math.hypot(fx, fy, fz) || 1;
      if (Math.abs(fy / fl) > 0.985) flips++;
    }
    this.buffer.clear();
    return { kind, dist: Math.max(2, opts.dist ?? 26), sinkDespawn: !!opts.sinkDespawn, duration: +total.toFixed(2), beats: cut.beats.map((b) => b.kind), byBullet: cut.byBullet, anyNaN, minCamHeight: +minCamH.toFixed(2), minShipClip: +minClip.toFixed(2), maxFrameJump: +maxJump.toFixed(2), nearVerticalFrames: flips };
  }

  private async trigger(frozen: FrozenReplay, heroId: number): Promise<void> {
    if (this.phase !== 'idle' || frozen.frames.length < 2) return;
    this.frozen = frozen;
    this.heroId = heroId;
    this.lastKnown.clear(); // fresh hold-cache per replay (never carry a prior clip's stale positions)
    this.cut = composeCut(frozen, heroId);
    this.victimCls = this.classOf(frozen, this.cut.fatal.victimId); // the doomed hull, for the sink cinematic
    try { this.renderer.clearWrecks(); } catch { /* renderer busy */ } // clean slate — the replay drives its OWN wreck
    try { this.renderer.setDynamicWrecksVisible(false); } catch { /* renderer busy */ } // hide the death-spot divable wreck so it never pops in before the sink cinematic
    this.phase = 'playing';
    this.R = 0; this.firedImpact = false; this.firedMuzzle = false; this.spawnedWreck = false; this.fadeK = 0; this.recStarted = false; this.clip = null; this.skipHeld = false;
    this.origFov = this.renderer.camera.fov;
    this.dcam.baseFov = this.origFov;
    this.buildOverlay();
    this.showCinematic(true);
    this.gameCanvas.style.filter = gradeCss(this.cut.grade);
    // audio + recording (async: fetch the highlight track, then start the recorder)
    let audioStream: MediaStream | null = null;
    try { const a = await this.audio.beginReplay(this.cut.music, ReplayExport.supported()); audioStream = a.stream; } catch { audioStream = null; }
    this.hadAudio = !!(audioStream && audioStream.getAudioTracks().length);
    if (ReplayExport.supported()) { try { await this.exporter.loadWordmark(); this.recStarted = this.exporter.begin(true, audioStream); } catch { this.recStarted = false; } }
    // if we're already past the intended start (async took a beat), that's fine — R started at 0 and the loop drives it
  }

  /** Driven from the main loop while active (mirrors dragaScene.frame). `dt` = real frame seconds. */
  frame(dt: number): void {
    if (this.phase === 'idle' || !this.cut || !this.frozen) return;
    const cut = this.cut;
    if (this.phase === 'playing') {
      this.R += dt * (this.skipHeld ? 3.2 : 1);
      if (this.R >= cut.totalReal) { this.R = cut.totalReal; this.endPlayback(); }
    }
    // scrub time from the active beat's ramp
    const i = DirectorCamera.beatAt(cut, Math.min(this.R, cut.totalReal - 1e-4));
    const beat = cut.beats[i]!;
    const scrubT = scrubTimeAt(beat, this.R - beat.realStart);
    this.curScrubT = scrubT;

    // sample the frozen scene → feed the renderer (everything interpolated; invariant #1)
    sampleScene(this.frozen, scrubT, this.pools);
    const p = this.pools;
    const local: LocalShipView | null = p.hasSelf ? { x: p.self.x, y: p.self.y, heading: p.self.heading, sail: p.self.sail, cls: p.self.cls } : null;
    this._remotes.length = p.shipN;
    for (let k = 0; k < p.shipN; k++) this._remotes[k] = p.ships[k] as unknown as InterpShip;
    this._balls.length = p.ballN;
    for (let k = 0; k < p.ballN; k++) this._balls[k] = p.balls[k] as unknown as InterpBall;
    this.renderer.syncShips(local, this.heroId, this._remotes, scrubT, 0, p.hasSelf ? p.self.hp : 100, false);
    this.renderer.syncBalls(this._balls, scrubT);

    // BEAT 1 muzzle FX + BEAT 4 sink cinematic: re-fire the killer's broadside flash and kick off the victim's
    // wreck at the right sim-time so the clip SHOWS the guns fire and films the naufrágio to the end.
    this.maybeMuzzle(cut, scrubT);
    this.maybeSink(cut, scrubT);

    // camera from the director
    const cam = this.dcam.sample(cut, Math.min(this.R, cut.totalReal - 1e-4), this.lookup, this.seaY, REPLAY.CROSSFADE_S);
    this.renderer.camera.position.set(cam.px, cam.py, cam.pz);
    this.renderer.camera.lookAt(cam.lx, cam.ly, cam.lz);
    if (Math.abs(this.renderer.camera.fov - cam.fov) > 0.02) { this.renderer.camera.fov = cam.fov; this.renderer.camera.updateProjectionMatrix(); }
    this.renderer.render(scrubT, dt, cam.lx, cam.lz);

    // the burst — fire once as the bullet-time release lands (or the ram beat mid-point)
    this.maybeImpact(cut, this.R, scrubT);

    // closing fade-to-black over the final REPLAY.FADE_S (the owner's beat #5) — into the export + on-screen
    this.fadeK = clamp01((this.R - (cut.totalReal - REPLAY.FADE_S)) / Math.max(1e-3, REPLAY.FADE_S));

    // export composite + on-screen overlays
    if (this.recStarted) this.exporter.composite(cut.grade, cam.vignette, REPLAY.LETTERBOX_FRAC, this.fadeK);
    this.updateOverlay(cam.vignette);
  }

  /** BEAT 1 — re-spawn the killer's muzzle-flash FX once, as the muzzle beat plays (so the clip shows the guns fire). */
  private maybeMuzzle(cut: Cut, scrubT: number): void {
    if (this.firedMuzzle) return;
    const beat = cut.beats.find((b) => b.kind === 'muzzle'); if (!beat) { this.firedMuzzle = true; return; }
    if (scrubT >= beat.scrubEnd - 0.05) { this.firedMuzzle = true; try { this.renderer.spawnFire(beat.aimX, beat.aimY, scrubT, 0); this.renderer.spawnFire(beat.aimX, beat.aimY, scrubT, 0); } catch { /* renderer busy */ } }
  }

  /** BEAT 4 — kick off the victim's sink cinematic once, at the sink moment, on the SCRUBBED clock (so the wreck
   *  ages with the replay and the full naufrágio plays). The camera holds the victim's last-known position. */
  private maybeSink(cut: Cut, scrubT: number): void {
    if (this.spawnedWreck) return;
    if (scrubT < cut.sinkStart) return;
    this.spawnedWreck = true;
    const at = this.lastKnown.get(cut.fatal.victimId);
    const x = at ? at.x : cut.fatal.x, y = at ? at.y : cut.fatal.y;
    try { if (!this.renderer.isWreck(cut.fatal.victimId)) this.renderer.spawnWreck(cut.fatal.victimId, x, y, scrubT, this.victimCls); } catch { /* renderer busy */ }
  }

  /** The doomed hull's class from the frozen tape (its last-known cls), for the wreck mesh. */
  private classOf(frozen: FrozenReplay, id: number): number {
    for (let i = frozen.frames.length - 1; i >= 0; i--) {
      const f = frozen.frames[i]!;
      if (f.self && f.self.id === id) return f.self.cls;
      for (const s of f.ships) if (s.id === id) return s.cls;
    }
    return 1;
  }

  // HOLD-last-known transforms for the camera principals: when a ship (or the hero ball) despawns mid-clip — which
  // is exactly what a sinking hull does — the camera keeps framing its LAST position instead of snapping/flying.
  private lastKnown = new Map<number, { x: number; y: number; heading: number }>();
  private lookup: Lookup = (id: number) => {
    const p = this.pools;
    let hit: { x: number; y: number; heading?: number } | null = null;
    if (p.hasSelf && p.self.id === id) hit = p.self;
    if (!hit) for (let k = 0; k < p.shipN; k++) if (p.ships[k]!.id === id) { hit = p.ships[k]!; break; }
    if (!hit) for (let k = 0; k < p.ballN; k++) if (p.balls[k]!.id === id) { hit = p.balls[k]!; break; }
    if (hit) {
      const c = this.lastKnown.get(id); const hd = (hit as { heading?: number }).heading ?? 0;
      if (c) { c.x = hit.x; c.y = hit.y; c.heading = hd; } else this.lastKnown.set(id, { x: hit.x, y: hit.y, heading: hd }); // copy (pools are reused)
      return hit;
    }
    return this.lastKnown.get(id) ?? null; // despawned → hold the last-known transform (anti-jump through the sink)
  };
  private seaY = (x: number, y: number): number => this.renderer.oceanY(x, y, this.curScrubT);

  private maybeImpact(cut: Cut, R: number, scrubT: number): void {
    if (this.firedImpact || !cut.byBullet) { if (!cut.byBullet) this.maybeImpactNonBullet(cut, R, scrubT); return; }
    // find the bullet beat + its release start
    const beat = cut.beats.find((b) => b.warp === 'bullet'); if (!beat) return;
    const releaseStart = beat.realStart + BULLET_TIME.APPROACH_S + BULLET_TIME.RAMP_IN_S + BULLET_TIME.ORBIT_S + BULLET_TIME.HOLD_S + BULLET_TIME.RELEASE_S * 0.6;
    if (R >= releaseStart) { this.firedImpact = true; this.burst(beat.aimX, beat.aimY, scrubT); }
  }
  private maybeImpactNonBullet(cut: Cut, R: number, scrubT: number): void {
    if (this.firedImpact) return;
    // a ram kill gets a collision burst mid-ram; a flood/no-attacker sink relies on the wreck's own break FX
    const beat = cut.beats.find((b) => b.kind === 'ram'); if (!beat) { this.firedImpact = true; return; }
    if (R >= beat.realStart + beat.realDur * 0.35) { this.firedImpact = true; this.burst(beat.aimX || cut.fatal.x, beat.aimY || cut.fatal.y, scrubT); }
  }
  private burst(x: number, y: number, t: number): void {
    try { this.renderer.spawnHit(x, y, t, true, 0, 0); this.renderer.spawnHit(x, y, t, true, 1, 1); this.renderer.addShake(1.6); } catch { /* renderer busy */ }
    this.haptic([30, 40, 60]);
    if (this.vig) { this.vig.style.transition = 'none'; this.vig.style.background = 'radial-gradient(circle at 50% 46%, rgba(255,240,200,.5), transparent 55%)'; void this.vig.offsetWidth; this.vig.style.transition = 'opacity .5s'; }
  }

  private endPlayback(): void {
    if (this.phase !== 'playing') return;
    this.phase = 'ended';
    void (async () => {
      this.clipMime = this.exporter.mimeType();
      if (this.recStarted) { try { this.clip = await this.exporter.finish(); } catch { this.clip = null; } }
      this.audio.endReplay();
      this.showCard();
    })();
  }

  /* --------------------------------- overlays --------------------------------- */

  private buildOverlay(): void {
    if (this.root) return;
    this.ensureStyle();
    const root = document.createElement('div'); root.id = 'killcam'; this.root = root;
    root.innerHTML = `
      <div class="kc-bar kc-top"></div>
      <div class="kc-bar kc-bot"></div>
      <div class="kc-vig"></div>
      <div class="kc-fade"></div>
      <div class="kc-title"></div>
      <button class="kc-skip">Skip ▸</button>
      <div class="kc-card"></div>`;
    document.body.appendChild(root);
    this.barTop = root.querySelector('.kc-top')!; this.barBot = root.querySelector('.kc-bot')!;
    this.vig = root.querySelector('.kc-vig')!; this.fadeEl = root.querySelector('.kc-fade')!; this.titleEl = root.querySelector('.kc-title')!;
    this.skipEl = root.querySelector('.kc-skip')!; this.card = root.querySelector('.kc-card')!;
    const hold = (v: boolean) => (e: Event) => { e.preventDefault(); this.skipHeld = v; };
    this.skipEl.addEventListener('pointerdown', hold(true));
    this.skipEl.addEventListener('pointerup', hold(false));
    this.skipEl.addEventListener('pointercancel', hold(false));
  }

  private showCinematic(on: boolean): void {
    if (this.root) this.root.style.display = on ? 'block' : 'none';
    // hide the sea HUD while the cinematic owns the screen
    this.overlay.style.visibility = on ? 'hidden' : '';
    document.body.classList.toggle('killcam-on', on);
    if (this.card) this.card.style.display = 'none';
    if (this.titleEl && this.cut) { this.titleEl.textContent = this.cut.title; this.titleEl.style.opacity = '1'; setTimeout(() => { if (this.titleEl) this.titleEl.style.opacity = '0'; }, 2200); }
  }

  private updateOverlay(vignette: number): void {
    if (!this.root) return;
    // bars ease in over the first ~0.4s
    const barIn = Math.min(1, this.R / 0.4);
    const barH = `${(REPLAY.LETTERBOX_FRAC * 100 * barIn).toFixed(2)}vh`;
    this.barTop.style.height = barH; this.barBot.style.height = barH;
    const v = Math.max(this.cut?.grade.vignette ?? 0, vignette);
    this.vig.style.opacity = String(0.35 + v * 0.65);
    if (this.skipEl) this.skipEl.style.opacity = this.phase === 'playing' && this.R > 0.6 && this.fadeK < 0.5 ? '0.85' : '0';
    // closing fade-to-black on-screen (mirrors the baked-in export fade)
    if (this.fadeEl) this.fadeEl.style.opacity = this.fadeK.toFixed(3);
  }

  private showCard(): void {
    if (!this.card) return;
    const canShare = !!this.clip;
    this.card.innerHTML = `
      <div class="kc-frame">
        <div class="kc-badge">★ HIGHLIGHT</div>
        <div class="kc-h">${this.cut?.title ?? 'Highlight'}</div>
        <div class="kc-sub">${canShare ? 'Your clip is ready — save the video or share it.' : 'Recording isn’t supported in this browser.'}</div>
        <div class="kc-btns">
          ${canShare ? '<button class="kc-b kc-b-primary" data-a="save">⬇ Save video</button>' : ''}
          ${canShare ? '<button class="kc-b kc-b-share" data-a="share">↗ Share</button>' : ''}
          <button class="kc-b kc-b-min" data-a="replay">Watch again</button>
          <button class="kc-b kc-b-min" data-a="close">Close</button>
        </div>
      </div>`;
    this.card.style.display = 'flex';
    this.skipEl.style.opacity = '0';
    this.card.querySelectorAll<HTMLButtonElement>('.kc-b').forEach((b) => b.addEventListener('click', () => this.onCard(b.dataset.a || 'close')));
  }

  private onCard(action: string): void {
    this.audio.ui();
    if (action === 'share' && this.clip) { void shareReplay(this.clip, this.clipMime, `${this.cut?.title ?? 'Highlight'} — play Sea of Solana at seaofrobin.quest`); return; }
    if (action === 'save' && this.clip) { downloadReplay(this.clip, this.clipMime); return; }
    if (action === 'replay') { this.replayAgain(); return; }
    this.close();
  }

  private replayAgain(): void {
    // re-run the SAME cut on screen (no re-record — the clip is already captured)
    this.phase = 'playing'; this.R = 0; this.firedImpact = false; this.firedMuzzle = false; this.spawnedWreck = false; this.fadeK = 0; this.recStarted = false;
    try { this.renderer.clearWrecks(); } catch { /* renderer busy */ } // fresh sink cinematic on the re-watch
    if (this.fadeEl) this.fadeEl.style.opacity = '0';
    if (this.card) this.card.style.display = 'none';
  }

  /** Exit the replay cleanly — restore the camera lens, the HUD and the game loop. */
  close(): void {
    if (this.phase === 'idle') return;
    this.phase = 'idle';
    if (this.recStarted) { void this.exporter.finish(); this.recStarted = false; }
    this.audio.endReplay();
    this.renderer.camera.fov = this.origFov; this.renderer.camera.updateProjectionMatrix();
    this.gameCanvas.style.filter = '';
    try { this.renderer.clearWrecks(); } catch { /* renderer busy */ } // drop the replay's wreck so it never bleeds into the resumed sea
    try { this.renderer.setDynamicWrecksVisible(true); } catch { /* renderer busy */ } // restore the divable death-spot wrecks for the resumed sea
    if (this.fadeEl) this.fadeEl.style.opacity = '0';
    this.fadeK = 0;
    this.showCinematic(false);
    this.cut = null; this.frozen = null; this.clip = null;
  }

  private ensureStyle(): void {
    if (document.getElementById('killcam-style')) return;
    const st = document.createElement('style'); st.id = 'killcam-style'; st.textContent = KILLCAM_CSS; document.head.appendChild(st);
  }

  /** DEV debug hook (window.__cc.dumpKill): freeze the CURRENT rolling tape at the last-armed kill (or the latest
   *  frame if none is armed) and return a serializable KillTape — the exact FrozenReplay the director consumes plus
   *  measured feature labels — for the offline kill-cam tuning bench (tools/killcam-bench). Zero cost until called;
   *  never touches netcode/sim. Returns null if there isn't enough tape yet. `heroId` defaults to the armed hero. */
  dumpTape(heroId?: number): KillTape | null {
    if (this.phase !== 'idle' || !this.buffer.ready()) return null;
    const killT = this.pendingKillT || this.buffer.latestT();
    if (killT <= 0) return null;
    const frozen = this.buffer.freeze(killT);
    if (frozen.frames.length < 2) return null;
    return buildTape(frozen, heroId ?? this.armedHeroId ?? this.heroId ?? 0, PROTOCOL_VERSION, { source: 'client/__cc.dumpKill' });
  }

  /** Headless / render-proof accessor: cut composition + the exported clip's specs. */
  debug(): { phase: Phase; R: number; total: number; beats: number; beatKinds: string[]; mood: string; music: string; byBullet: boolean; title: string; supported: boolean; recording: boolean; hasClip: boolean; clipBytes: number; clipMime: string; hadAudio: boolean; outW: number; outH: number } {
    const c = this.cut;
    return {
      phase: this.phase, R: +this.R.toFixed(3), total: c ? +c.totalReal.toFixed(3) : 0,
      beats: c ? c.beats.length : 0, beatKinds: c ? c.beats.map((b) => b.kind) : [],
      mood: c?.mood ?? '', music: c?.music ?? '', byBullet: c?.byBullet ?? false, title: c?.title ?? '',
      supported: ReplayExport.supported(), recording: this.recStarted, hasClip: !!this.clip,
      clipBytes: this.clip?.size ?? 0, clipMime: this.clipMime, hadAudio: this.hadAudio,
      outW: REPLAY.OUT_W, outH: REPLAY.OUT_H,
    };
  }
}

/* --------------------------------- css / grade helper --------------------------------- */

function gradeCss(g: { saturation: number; brightness: number; warm: number }): string {
  const sepia = g.warm > 0 ? ` sepia(${g.warm.toFixed(3)})` : '';
  return `saturate(${g.saturation.toFixed(3)}) brightness(${g.brightness.toFixed(3)}) contrast(1.05)${sepia}`;
}

const KILLCAM_CSS = `
@keyframes kcpulse{ 0%,100%{ box-shadow:0 0 0 0 rgba(245,215,120,.6),0 10px 28px rgba(0,0,0,.6),inset 0 1px 0 rgba(255,240,190,.25); transform:translateX(-50%) scale(1);} 50%{ box-shadow:0 0 0 13px rgba(245,215,120,0),0 10px 28px rgba(0,0,0,.6),inset 0 1px 0 rgba(255,240,190,.25); transform:translateX(-50%) scale(1.04);} }
/* ★ SAVE HIGHLIGHT prompt — big, explicit, PERSISTENT, low-centre (clear of the crowded top HUD strip) so a
   mobile captain fresh off a kill can't miss it. Two-line label: what it is + what to do. */
#kc-arm{ position:fixed; left:50%; transform:translateX(-50%); bottom:calc(env(safe-area-inset-bottom,10px) + 200px); z-index:130; display:none; align-items:center; gap:12px; min-height:58px; min-width:236px; max-width:88vw; padding:13px 26px 13px 20px; box-sizing:border-box; border-radius:20px; border:2px solid rgba(245,215,120,.92); background:linear-gradient(180deg,rgba(74,51,17,.98),rgba(40,27,8,.985)); color:#f7dd84; font-family:'Cinzel',Georgia,serif; text-align:left; text-shadow:0 1px 2px #000; cursor:pointer; -webkit-tap-highlight-color:transparent; animation:kcpulse 1.4s ease-in-out infinite; }
#kc-arm:active{ filter:brightness(1.12); }
#kc-arm .kc-arm-star{ font-size:30px; line-height:1; color:#ffe9a6; text-shadow:0 0 14px rgba(245,215,120,.85),0 1px 2px #000; }
#kc-arm .kc-arm-txt{ display:flex; flex-direction:column; align-items:flex-start; gap:2px; }
#kc-arm .kc-arm-title{ font-size:18px; font-weight:800; letter-spacing:.06em; line-height:1.05; }
#kc-arm .kc-arm-sub{ font-family:Georgia,serif; font-style:italic; font-weight:400; font-size:12.5px; letter-spacing:.01em; color:#ecd49a; opacity:.94; }
#killcam{ position:fixed; inset:0; z-index:150; display:none; pointer-events:none; overflow:hidden; font-family:'Cinzel',Georgia,serif; }
#killcam .kc-bar{ position:absolute; left:0; right:0; height:0; background:#000; z-index:2; transition:height .12s linear; }
#killcam .kc-top{ top:0; } #killcam .kc-bot{ bottom:0; }
#killcam .kc-vig{ position:absolute; inset:0; z-index:1; pointer-events:none; opacity:.4; background:radial-gradient(circle at 50% 46%, transparent 38%, rgba(0,3,8,.7) 88%, rgba(0,2,6,.92) 100%); transition:opacity .3s; }
#killcam .kc-fade{ position:absolute; inset:0; z-index:4; pointer-events:none; opacity:0; background:#000; }
#killcam .kc-title{ position:absolute; top:16%; left:0; right:0; text-align:center; z-index:3; color:#f5e6b0; font-weight:900; font-size:min(9vw,54px); letter-spacing:.14em; text-shadow:0 3px 16px rgba(0,0,0,.85),0 0 30px rgba(245,215,120,.35); opacity:0; transition:opacity .6s; }
#killcam .kc-skip{ position:absolute; right:16px; bottom:calc(env(safe-area-inset-bottom,10px) + 14px); z-index:4; pointer-events:auto; opacity:0; transition:opacity .3s; padding:8px 15px; border-radius:18px; border:1px solid rgba(210,175,100,.5); background:rgba(8,10,16,.55); color:#e8d9ac; font:inherit; font-size:12px; letter-spacing:.1em; cursor:pointer; }
#killcam .kc-card{ position:absolute; inset:0; z-index:5; display:none; align-items:center; justify-content:center; padding:24px; pointer-events:auto; }
#killcam .kc-frame{ width:min(360px,86vw); text-align:center; padding:22px 22px 18px; border-radius:14px; border:1px solid rgba(245,215,120,.45); background:linear-gradient(180deg,rgba(44,32,14,.97),rgba(28,20,8,.98)); box-shadow:0 0 40px rgba(245,215,120,.18),0 18px 50px rgba(0,0,0,.7); animation:kcpop .45s cubic-bezier(.2,1.3,.4,1) both; }
@keyframes kcpop{ from{ transform:translateY(20px) scale(.92); opacity:0 } to{ transform:none; opacity:1 } }
#killcam .kc-badge{ display:inline-block; padding:3px 12px; border-radius:3px; font-size:11px; letter-spacing:.24em; color:#241708; background:#f5d878; box-shadow:0 0 14px rgba(245,215,120,.6); }
#killcam .kc-h{ margin:12px 0 4px; font-size:26px; font-weight:900; color:#f5e6b0; letter-spacing:.06em; }
#killcam .kc-sub{ color:#c9b78f; font-family:Georgia,serif; font-style:italic; font-size:13px; margin-bottom:16px; }
#killcam .kc-btns{ display:flex; flex-wrap:wrap; gap:9px; justify-content:center; }
#killcam .kc-b{ flex:1 1 44%; min-width:120px; min-height:50px; padding:13px 12px; border-radius:11px; border:1px solid rgba(210,175,100,.5); background:linear-gradient(180deg,rgba(240,220,150,.22),rgba(150,110,40,.2)); color:#f2e2b0; font:inherit; font-weight:800; letter-spacing:.05em; font-size:15px; cursor:pointer; -webkit-tap-highlight-color:transparent; }
#killcam .kc-b-primary{ background:linear-gradient(180deg,#f3d873,#c99a3a); color:#2a1c08; box-shadow:0 4px 14px rgba(245,215,120,.35); }
#killcam .kc-b-share{ background:linear-gradient(180deg,rgba(245,225,160,.34),rgba(170,125,45,.3)); color:#fce9b4; }
#killcam .kc-b-min{ min-height:40px; padding:9px; font-size:13px; font-weight:700; opacity:.82; background:linear-gradient(180deg,rgba(200,185,140,.14),rgba(120,90,35,.14)); }
body.killcam-on #game{ z-index:140 !important; }
`;
